import { useContext, useEffect, useState } from "react"
import { DateTime } from "luxon"

//Assessment components
import { getFunctionByName } from "./Helpers/formulaHelpers"
import formulaBuilderTypes from "./formulaBuilderTypes"

//Context
import { AssessmentFormulaBuilderContext } from "../../../../../context/assessmentFormulaBuilderContext"

//PrimeReact
import { Divider } from "primereact/divider"

//Styles
import myStyles from "../../../../../styles/assessments/ManagementFormulaBuilder.module.css"

export default function FormulaBuilderPreview({ options }) {
  const { formulas } = useContext(AssessmentFormulaBuilderContext)

  const [preview, setPreview] = useState("")

  const getValue = () => {
    const min = 1
    const max = 100

    return Math.floor(Math.random() * (max - min) + min)
  }

  const getDateValue = () => {
    var dateValue = DateTime.now()

    return dateValue.toLocaleString()
  }

  const getConnectingOperator = (operator) => {
    let operatorProfile = formulaBuilderTypes.operators.find(
      (op) => op.name === operator
    )

    return operatorProfile?.display ?? ""
  }

  const getQuestionOrValue = (formula) => {
    let value = ""
    let operator = ""

    if (!formula.isDeleted) {
      if (formula.question) {
        value = getValue()
        operator = formula.question.connectingOperator
      } else if (formula.value) {
        value = formula.value.value
        operator = formula.value.connectingOperator
      }
    }
    return { formulaValue: value, operator }
  }

  const getOptionValue = (formula) => {
    let value = ""
    let operator = formula.option.connectingOperator

    const option = options?.find((opt) => opt.id === formula.option.optionId)

    if (option) {
      value = option.scoreValue ?? 0
    } else {
      value = "??"
    }

    return { formulaValue: value, operator }
  }

  const getFormulaCalc = (formula) => {
    let formulaCalc = ""
    const functionConfig = getFunctionByName(formula.function.type)

    if (formula.function?.values?.length > 0 && !formula.isDeleted) {
      let skipOperator = true

      formula.function.values.forEach((val) => {
        if (!val.isDeleted) {
          let value = ""

          //Calcualte the value based on the input type of the function
          if (functionConfig.acceptDateTime) {
            value = getDateValue()
          } else {
            value = getQuestionOrValue(val).formulaValue
          }

          //Process the value and operator
          if (value) {
            formulaCalc += `${!skipOperator ? ", " : ""}${value}`

            if (skipOperator) {
              skipOperator = false
            }
          }
        }
      })
    }

    if (formulaCalc.trim().length > 0) {
      formulaCalc = `${formula.function.type?.toUpperCase()}(${
        formula.function.attribute ? formula.function.attribute + "; " : ""
      }${formulaCalc.trim()})`
    }

    return {
      formulaValue: formulaCalc,
      operator: formula.function?.connectingOperator,
    }
  }

  const getFormulaGroupCalc = (formulaGroup) => {
    let groupCalc = ""

    let processedValue = undefined
    let skipOperator = true

    if (formulaGroup.formulas?.length > 0 && !formulaGroup.isDeleted) {
      //Loop through each formula in the group and build its calculation
      formulaGroup.formulas.forEach((formula) => {
        if (!formula.isDeleted) {
          if (formula.question || formula.value) {
            processedValue = getQuestionOrValue(formula)
          } else if (formula.function) {
            processedValue = getFormulaCalc(formula)
          } else if (formula.option) {
            processedValue = getOptionValue(formula)
          }

          if (processedValue?.formulaValue) {
            // Append the value and operator to the group calculation
            groupCalc += `${
              !skipOperator
                ? getConnectingOperator(processedValue?.operator) + " "
                : ""
            }${processedValue?.formulaValue} `

            // If the operator was not skipped, set skipOperator to false for the next iteration
            if (skipOperator) {
              skipOperator = false
            }
          }
        }
      })

      //Add the group function if specified
      if (groupCalc.trim().length > 0) {
        if (formulaGroup.groupFunctionType) {
          groupCalc = `${formulaGroup.groupFunctionType.toUpperCase()}(${groupCalc.trim()}`

          if (formulaGroup.groupFunctionAttribute !== undefined) {
            groupCalc += `, ${formulaGroup.groupFunctionAttribute}`
          }

          groupCalc += ")"
        } else {
          groupCalc = `(${groupCalc.trim()})`
        }
      }
    }

    return {
      formulaValue: groupCalc.trim(),
      operator: formulaGroup.connectingOperator,
    }
  }

  useEffect(() => {
    let newFormula = ""

    let processedValue = undefined
    let skipOperator = true

    formulas?.forEach((formula) => {
      if (!formula.isDeleted) {
        if (formula.question || formula.value) {
          processedValue = getQuestionOrValue(formula)
        } else if (formula.group) {
          processedValue = getFormulaGroupCalc(formula.group)
        } else if (formula.function) {
          processedValue = getFormulaCalc(formula)
        } else if (formula.option) {
          processedValue = getOptionValue(formula)
        }

        if (processedValue?.formulaValue) {
          newFormula += `${
            !skipOperator
              ? getConnectingOperator(processedValue?.operator) + " "
              : ""
          }${processedValue?.formulaValue} `

          if (skipOperator) {
            skipOperator = false
          }
        }
      }
    })

    setPreview("= " + newFormula)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formulas])

  return (
    <div className={myStyles.PreviewContainer}>
      <div className={`${myStyles.formLayout}`}>
        <div className={myStyles.formLayoutHeader}>
          <div className={myStyles.formHeader}>Preview</div>
          <Divider />
        </div>
        <div className={myStyles.formLayoutBody}>{preview}</div>
      </div>
    </div>
  )
}
